//libs
import React, { useCallback, useEffect, useRef, useState } from 'react';
import c from 'classnames';
import { NodeSelection } from 'prosemirror-state';
//types
import { EditorView } from 'prosemirror-view';

import { useTranslation } from '@lyearn/i18n';
//components
import {
  BaseMenuItem,
  BaseMenuList,
  BasePopoverContent,
  Box,
  DeprecatedIconButton,
  Stack,
  Tooltip,
  Typography,
} from '@lyearn/ui';

//consts
import {
  ANCHOR_ORIGIN,
  CLASSES,
  MEDIA_BLOCK_LAYOUTS,
  MEDIA_BLOCK_POPOVER_OPTIONS,
  TRANSFORM_ORIGIN,
} from './consts';
//styles
import { fillEvenOdd, iconButtonStyles } from './styles';
import { MediaBlockLayoutTypes, PopoverMenuItem } from './types';
import { useTooltipMenuAction, useTooltipMenuPopoverAction } from './useTooltipMenuAction';

interface MediaLayoutsProps {
  view: EditorView;
}

const MediaLayouts: React.FC<MediaLayoutsProps> = (props) => {
  const { view } = props;
  const [selectedLayout, setSelectedLayout] = useState<MediaBlockLayoutTypes | null>(null);
  const { t } = useTranslation();
  const anchorRef = useRef(new Map());
  const [popoverLayout, setPopoverLayout] = useState<MediaBlockLayoutTypes | null>(null);

  let width: null | number = null;
  let caption: null | string = null;
  let imageLayout: null | string = null;
  if (view) {
    width = (view.state.selection as NodeSelection).node?.attrs?.width;
    caption = (view.state.selection as NodeSelection).node?.attrs?.caption;
    imageLayout = (view.state.selection as NodeSelection).node?.attrs?.layout;
  }

  useEffect(() => {
    if (!width) {
      setSelectedLayout(MediaBlockLayoutTypes.Center);
    } else if (width === 100) {
      setSelectedLayout(MediaBlockLayoutTypes.FullWidth);
    } else {
      setSelectedLayout(null);
    }
  }, [width]);

  const toggle = useCallback(() => {
    setPopoverLayout(null);
  }, [setPopoverLayout]);

  const onAction = useTooltipMenuAction(view);
  const onPopoverAction = useTooltipMenuPopoverAction(view);

  return (
    <Stack alignItems="center" className="p-6 pe-2">
      {MEDIA_BLOCK_LAYOUTS(t).map((layout, key) => {
        if (!layout.Icon) {
          return <Box key={key} className="h-24 w-1 bg-border me-4" />;
        }
        const shouldHighlightSelectedLayout: boolean = layout.type === selectedLayout;
        const shouldHighlightCaption: boolean =
          layout.type === MediaBlockLayoutTypes.AddCaption && caption !== null;
        const highlightStyles: string =
          shouldHighlightSelectedLayout || shouldHighlightCaption ? 'bg-bg-tertiary' : '';
        return (
          <Tooltip key={key} title={layout.label}>
            <DeprecatedIconButton
              ref={(ele) => anchorRef.current.set(layout.type, ele)}
              className={c(
                iconButtonStyles,
                highlightStyles,
                'rounded-4 p-4 me-4 hover:text-icon-active',
              )}
              data-layout-id={layout.label}
              variant="ghost"
              onClick={(event) => onAction(layout.type, setPopoverLayout, event)}>
              <layout.Icon className={fillEvenOdd} />
            </DeprecatedIconButton>
          </Tooltip>
        );
      })}
      <BasePopoverContent
        anchorEl={popoverLayout ? anchorRef.current.get(popoverLayout) : null}
        anchorOrigin={ANCHOR_ORIGIN}
        classes={CLASSES}
        open={!!popoverLayout && !!anchorRef.current.get(popoverLayout)}
        transformOrigin={TRANSFORM_ORIGIN}
        onClose={toggle}>
        <BaseMenuList
          autoFocus={true}
          className="rounded-4 bg-bg-primary p-8 shadow-01 outline-none">
          {(MEDIA_BLOCK_POPOVER_OPTIONS(t) as any)[popoverLayout!]?.map(
            (menuItem: PopoverMenuItem, index: number) => {
              return (
                <BaseMenuItem
                  key={index}
                  className={c('rounded-4 px-10', index !== 0 ? 'mt-2' : '')}
                  data-id={menuItem.label}
                  selected={menuItem.label === imageLayout}
                  onClick={() => onPopoverAction(menuItem.type, toggle)}>
                  <menuItem.Icon className="h-20 w-20 text-icon-secondary" />
                  <Typography className="text-text-secondary ms-12" variant="body-short-02">
                    {menuItem.label}
                  </Typography>
                </BaseMenuItem>
              );
            },
          )}
        </BaseMenuList>
      </BasePopoverContent>
    </Stack>
  );
};

export default MediaLayouts;
